/**
 * Which service-owner messages this browser has already shown.
 *
 * Everything else in the inbox — rejected changes, failed AI processor runs,
 * failed webhooks — is acknowledged on the server, against the active
 * membership's `last_seen_notifications`. That anchor is a timestamp, and it
 * is the right mechanism: it follows the user between browsers and keeps a
 * separate position per team.
 *
 * Messages cannot use it. They have no date at all — `orcaexternal.Message`
 * has an `expiry_date` and nothing else temporal — so the backend lists them
 * through `filter_notifications(team)` with no `since` argument, filtered only
 * by expiry and audience. They are standing notices rather than events, and
 * the anchor structurally cannot describe them, which is why every reload
 * brought back the same messages the reader had already dismissed.
 *
 * So they are tracked here, by id, and only they. Two consequences worth
 * knowing: this is per-browser, so reading on a laptop leaves the message
 * unread on a phone; and it is the smaller half of the problem, because a
 * standing notice reappearing is a nuisance where a missed failure is not.
 * Moving this to the server means giving messages a date, or giving the
 * membership a seen-message set — either is a migration.
 */

const KEY_PREFIX = 'readMessageIds';

/** per team: a message is published to the consumers of one team's services */
const keyFor = (teamId: number | string | null | undefined) => `${KEY_PREFIX}:${teamId ?? 'none'}`;

const read = (teamId: number | string | null | undefined): number[] => {
    try {
        const raw = localStorage.getItem(keyFor(teamId));
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'number') : [];
    } catch (error) {
        // a corrupt entry is not worth failing the inbox over
        console.error('Could not read message ids', error);
        return [];
    }
};

const write = (teamId: number | string | null | undefined, ids: number[]) => {
    try {
        localStorage.setItem(keyFor(teamId), JSON.stringify(ids));
    } catch (error) {
        console.error('Could not store read message ids', error);
    }
};

export const getReadMessageIds = (teamId: number | string | null | undefined): Set<number> =>
    new Set(read(teamId));

/**
 * Record these as shown, and forget any the server no longer sends.
 *
 * The prune is what stops this growing without bound: a message that has
 * expired or been deleted stops being listed, so there is nothing left to
 * suppress and no reason to keep remembering it.
 */
export const markMessagesRead = (
    teamId: number | string | null | undefined,
    shownIds: number[],
    stillListedIds: number[],
) => {
    const listed = new Set(stillListedIds);
    const next = [...new Set([...read(teamId), ...shownIds])].filter((id) => listed.has(id));
    write(teamId, next);
};
